import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Alert, Button, OtpInput, PasswordField, TextField } from '@kcm/ui'
import { ErrorCode, PasswordResetRequest } from '@kcm/contracts'
import { Turnstile } from '../components/Turnstile'
import { useSite } from '../components/SiteProvider'
import { useTranslate } from '@tolgee/react'
import { useLocale } from '../hooks/useLocale'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'
import { asApiError, fieldError } from '../lib/validation'
import { maskEmail } from '../store/funnel'
import { titleFor } from './Login'

/**
 * Getting a password back (PLAN.md §10.2).
 *
 * `oldcode/forgot-password-pin.php:127` answered 404 `user_not_found` for an
 * address with no account, which turned this form into a free membership oracle
 * for the entire client base (finding #17). The server now issues a real
 * challenge either way, so this screen advances to the code step for every
 * address — there is nothing here that can tell the two apart, because there is
 * nothing in the response that can either.
 *
 * The code arrives by email and is six digits with a five-attempt cap, against
 * the legacy four digits with no counter at all (findings #4, #16). It is a
 * code and not a link on purpose: a reset URL survives in browser history, in
 * forwarded mail, and in the link scanners that follow it.
 */
interface Challenge {
  id: string
  canEscalate: boolean
}

export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [done, setDone] = useState(false)

  if (done) return <Confirmation />

  return challenge ? (
    <ResetPhase
      email={email}
      challenge={challenge}
      onChallenge={setChallenge}
      onDone={() => setDone(true)}
      onRestart={() => setChallenge(null)}
    />
  ) : (
    <AddressPhase email={email} onEmail={setEmail} onChallenge={setChallenge} />
  )
}

// ── phase 1: the address ─────────────────────────────────────────────────────

function AddressPhase({
  email,
  onEmail,
  onChallenge,
}: {
  email: string
  onEmail: (email: string) => void
  onChallenge: (challenge: Challenge) => void
}) {
  const site = useSite()
  const { t } = useTranslate()
  const locale = useLocale()
  const [token, setToken] = useState('')
  const [widget, setWidget] = useState(0)
  const [challengeUnavailable, setChallengeUnavailable] = useState(false)

  const request = useMutation({
    mutationFn: () =>
      endpoints.password.forgot({
        email: email.trim().toLowerCase(),
        ...(token ? { turnstileToken: token } : {}),
        locale,
      }),
    onSuccess: (res) => onChallenge({ id: res.challengeId, canEscalate: res.canEscalate }),
    onError: () => {
      setToken('')
      setWidget((n) => n + 1)
    },
  })

  const error = request.error instanceof ApiError ? request.error : null
  const blocked = site.turnstileSiteKey !== null && !token && !challengeUnavailable

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!request.isPending) request.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">{t('forgot.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('forgot.subtitle')}</p>
      </div>

      {error && (
        <Alert tone="error" title={titleFor(error)}>
          {error.message}
        </Alert>
      )}

      <TextField
        label="Email address"
        type="email"
        name="email"
        inputMode="email"
        autoComplete="username"
        autoFocus
        required
        value={email}
        error={fieldError(error, 'email')}
        onChange={(e) => onEmail(e.target.value)}
      />

      {site.turnstileSiteKey && (
        <Turnstile
          siteKey={site.turnstileSiteKey}
          resetKey={widget}
          locale={locale}
          onToken={(next) => {
            setToken(next)
            if (next) setChallengeUnavailable(false)
          }}
          onUnavailable={() => setChallengeUnavailable(true)}
        />
      )}

      {challengeUnavailable && !error && (
        <Alert tone="info" title="The bot check didn't load">
          You can still continue — we'll confirm it when you submit.
        </Alert>
      )}

      <Button type="submit" disabled={request.isPending || email.trim() === '' || blocked}>
        {request.isPending ? 'Sending…' : 'Send code'}
      </Button>

      <Link className="text-sm text-muted underline" to="/login">
        {t('forgot.backToSignIn')}
      </Link>
    </form>
  )
}

// ── phase 2: the code and the new password ───────────────────────────────────

/**
 * One form, not two.
 *
 * The server takes the code and the new password in a single call and spends
 * the challenge on the way through, so splitting them would mean holding a
 * verified code in the browser between two screens for no gain.
 */
function ResetPhase({
  email,
  challenge,
  onChallenge,
  onDone,
  onRestart,
}: {
  email: string
  challenge: Challenge
  onChallenge: (challenge: Challenge) => void
  onDone: () => void
  onRestart: () => void
}) {
  const site = useSite()
  const { t } = useTranslate()
  const locale = useLocale()
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [escalated, setEscalated] = useState(false)

  const reset = useMutation({
    mutationFn: async () => {
      const parsed = PasswordResetRequest.safeParse({ challengeId: challenge.id, code, password })
      // Caught here so a weak password costs no round trip — and because a raw
      // ZodError is not an ApiError and would render as nothing at all.
      if (!parsed.success) throw asApiError(parsed.error)
      return endpoints.password.reset(parsed.data)
    },
    onSuccess: onDone,
    onError: () => setCode(''),
  })

  const resend = useMutation({
    mutationFn: (escalate: boolean) =>
      endpoints.password.forgot({
        email: email.trim().toLowerCase(),
        escalate,
        locale,
      }),
    onSuccess: (res, escalate) => {
      onChallenge({ id: res.challengeId, canEscalate: res.canEscalate })
      if (escalate) setEscalated(true)
    },
  })

  const raw = reset.error ?? resend.error
  const error = raw instanceof ApiError ? raw : null

  // The challenge is gone for good; a new one has to start from the address.
  const dead = error?.code === ErrorCode.OTP_EXPIRED || error?.code === ErrorCode.OTP_LOCKED

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!reset.isPending) reset.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">{t('forgot.newTitle')}</h1>
        <p className="mt-1 text-sm text-muted">
          {/*
            Says "if you have an account" because the server does not tell us
            whether one exists, and a screen that stated it plainly would be the
            oracle this flow exists to close.
          */}
          If there's an account for {email ? maskEmail(email) : 'that address'}, a code is on its
          way.
        </p>
      </div>

      {error && (
        <Alert tone="error" title={titleFor(error)}>
          {error.message}
          {fieldError(error, 'code') && <span className="block">{fieldError(error, 'code')}</span>}
        </Alert>
      )}

      {escalated && !error && (
        <Alert tone="info">We've sent another code from a different provider.</Alert>
      )}

      {dead ? (
        <Button onClick={onRestart}>Start again</Button>
      ) : (
        <>
          <OtpInput value={code} onChange={setCode} />

          {/*
            Carried alongside the code so a password manager has a pair to save.
            `readOnly` rather than hidden: a manager ignores what it cannot see,
            and it is the last chance to notice the code went to the wrong
            address.
          */}
          {email && (
            <TextField
              label="Email address"
              type="email"
              name="email"
              autoComplete="username"
              readOnly
              value={email}
              className="bg-surface-alt text-muted"
            />
          )}

          <PasswordField
            label="New password"
            required
            name="new-password"
            autoComplete="new-password"
            value={password}
            error={fieldError(error, 'password')}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button
            type="submit"
            disabled={reset.isPending || code.length !== 6 || password === ''}
          >
            {reset.isPending ? 'Saving…' : 'Set new password'}
          </Button>

          <div className="flex flex-col gap-2 text-sm">
            <button
              type="button"
              className="text-brand underline disabled:text-muted"
              disabled={resend.isPending}
              onClick={() => resend.mutate(false)}
            >
              Send the code again
            </button>
            {/* Escalation, not a retry: the provider that already reported
                success will keep reporting success (§8). */}
            {challenge.canEscalate && (
              <button
                type="button"
                className="text-brand underline disabled:text-muted"
                disabled={resend.isPending || escalated}
                onClick={() => resend.mutate(true)}
              >
                I didn't receive it — try another way
              </button>
            )}
            <button type="button" className="text-muted underline" onClick={onRestart}>
              Use a different email address
            </button>
          </div>
        </>
      )}
    </form>
  )
}

// ── phase 3: done ────────────────────────────────────────────────────────────

function Confirmation() {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-5">
      <Alert tone="success" title="Password changed">
        {/*
          Worth saying, because it is the one thing a person whose account was
          being taken over needs to know happened: the server ends every other
          session for this account after a reset.
        */}
        You've been signed out everywhere else. Sign in with your new password.
      </Alert>

      <Button onClick={() => navigate('/login')}>Sign in</Button>
    </div>
  )
}
